import { useState, useEffect, useCallback } from 'react';
import { getContacts, updateContact, downloadContacts, getInteractions, logInteraction } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './Contacts.css';

const SORT_FIELDS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'companyName', label: 'Company' },
  { value: 'email', label: 'Email' },
];

const EMPTY_FILTERS = {
  name: '', designation: '', companyName: '', location: '', status: [],
  purpose: '', createdBy: '', dateFrom: '', dateTo: '', interaction: '',
  sortField: 'createdAt', sortDir: 'desc',
};

const EMPTY_EDIT = {
  companyName: '', companyUrl: '', industry: '', companyLinkedinUrl: '',
  location: '', employeeCount: '', firstName: '', lastName: '',
  designation: '', linkedinUrl: '', email: '', purpose: '', status: '',
};

const EMPTY_LOG = { channel: 'Email', actionTaken: '', notes: '', outcome: 'Sent' };

const STATUS_OPTIONS = ['ACTIVE', 'DNC', 'BOUNCE'];
const CHANNELS = ['Email', 'LinkedIn', 'Phone', 'WhatsApp', 'Other'];
const OUTCOMES = ['Sent', 'Replied', 'Meeting Fixed', 'Interested', 'Not Interested', 'No Response'];

const CHANNEL_COLORS = {
  email: '#2563eb', linkedin: '#0077b5', phone: '#16a34a',
  whatsapp: '#25d366', other: '#6b7280',
};
const OUTCOME_COLORS = {
  sent: '#2563eb', replied: '#7c3aed', 'meeting fixed': '#0891b2',
  interested: '#16a34a', 'not interested': '#dc2626', 'no response': '#6b7280',
};

const badgeColor = (map, key = '') =>
  map[(key ?? '').toLowerCase()] ?? '#6b7280';

export default function Contacts({ domainFilter = null, onClearDomainFilter }) {
  const { auth } = useAuth();
  const isAdmin = auth?.role === 'ROLE_ADMIN';

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [statusOpen, setStatusOpen] = useState(false);

  /* ── Edit modal ── */
  const [editContact, setEditContact] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  /* ── View modal ── */
  const [viewContact, setViewContact] = useState(null);
  const [viewTab, setViewTab] = useState('details');
  const [interactions, setInteractions] = useState([]);
  const [intLoading, setIntLoading] = useState(false);
  const [intError, setIntError] = useState('');
  const [showLogModal, setShowLogModal] = useState(false);
  const [logForm, setLogForm] = useState(EMPTY_LOG);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState('');

  const [downloading, setDownloading] = useState(false);

  /* ── Fetch contacts ── */
  const fetchContacts = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { page, size: pageSize, sort: `${applied.sortField},${applied.sortDir}` };
      if (search.trim()) params.search = search.trim();
      if (applied.purpose.trim()) params.purpose = applied.purpose.trim();
      if (applied.createdBy.trim()) params.createdBy = applied.createdBy.trim();
      if (applied.dateFrom) params.dateFrom = applied.dateFrom;
      if (applied.dateTo) params.dateTo = applied.dateTo;
      if (applied.name.trim()) params.name = applied.name.trim();
      if (applied.designation.trim()) params.designation = applied.designation.trim();
      if (applied.companyName.trim()) params.companyName = applied.companyName.trim();
      if (applied.location.trim()) params.location = applied.location.trim();
      if (applied.status && applied.status.length > 0) params.status = applied.status.join(',');
      if (applied.interaction) params.interaction = applied.interaction;
      if (domainFilter) params.domain = domainFilter;
      const { data } = await getContacts(params);
      setContacts(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (e) {
      setError(e.message || 'Failed to load contacts.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, applied, search, domainFilter]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  /* Close status dropdown on click outside */
  useEffect(() => {
    if (!statusOpen) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.ct-multi-select')) {
        setStatusOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusOpen]);

  /* ── Filters ── */
  const activeFilterCount = [applied.name, applied.designation, applied.companyName, applied.location, applied.status.length > 0, applied.purpose, applied.createdBy, applied.dateFrom, applied.dateTo, applied.interaction].filter(Boolean).length;
  const openDrawer = () => { setDraft({ ...applied }); setDrawerOpen(true); };
  const closeDrawer = () => setDrawerOpen(false);
  const applyFilters = () => { setApplied({ ...draft }); setPage(0); closeDrawer(); };
  const resetFilters = () => { setDraft(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(0); closeDrawer(); };
  const handleDraft = (e) => { const { name, value } = e.target; setDraft(d => ({ ...d, [name]: value })); };

  const handleSort = (field) => {
    const dir = field === applied.sortField && applied.sortDir === 'asc' ? 'desc' : 'asc';
    setApplied(a => ({ ...a, sortField: field, sortDir: dir }));
    setPage(0);
  };

  /* ── Edit ── */
  const openEdit = (c) => {
    setEditContact(c);
    setEditForm(Object.fromEntries(Object.keys(EMPTY_EDIT).map(k => [k, c[k] ?? ''])));
    setEditError('');
  };
  const closeEdit = () => { setEditContact(null); setEditForm(EMPTY_EDIT); setEditError(''); };

  const handleEditChange = (e) => { const { name, value } = e.target; setEditForm(f => ({ ...f, [name]: value })); };

  const handleEditSave = async () => {
    setEditLoading(true); setEditError('');
    try {
      const payload = {};
      Object.keys(editForm).forEach(k => {
        if (editForm[k] !== (editContact[k] ?? '')) payload[k] = editForm[k] || null;
      });
      if (Object.keys(payload).length === 0) { closeEdit(); return; }
      await updateContact(editContact.id, payload);
      closeEdit(); fetchContacts();
    } catch (e) {
      setEditError(e.message || 'Failed to update contact.');
    } finally {
      setEditLoading(false);
    }
  };

  /* ── View ── */
  const openView = async (c) => {
    setViewContact(c);
    setViewTab('details');
    setInteractions([]);
    setIntError('');
  };
  const closeView = () => { setViewContact(null); setShowLogModal(false); };

  const loadInteractions = useCallback(async (id) => {
    setIntLoading(true); setIntError('');
    try {
      const { data } = await getInteractions(id);
      setInteractions(data);
    } catch (e) {
      setIntError(e.message || 'Failed to load interactions.');
    } finally {
      setIntLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewContact && viewTab === 'interactions') {
      loadInteractions(viewContact.id);
    }
  }, [viewContact, viewTab, loadInteractions]);

  /* ── Log Interaction ── */
  const openLog = () => { setLogForm(EMPTY_LOG); setLogError(''); setShowLogModal(true); };
  const closeLog = () => setShowLogModal(false);

  const handleLogChange = (e) => { const { name, value } = e.target; setLogForm(f => ({ ...f, [name]: value })); };

  const handleLogSubmit = async () => {
    if (!logForm.actionTaken.trim()) { setLogError('Action taken is required.'); return; }
    setLogLoading(true); setLogError('');
    try {
      await logInteraction(viewContact.id, logForm);
      closeLog();
      loadInteractions(viewContact.id);
    } catch (e) {
      setLogError(e.message || 'Failed to log interaction.');
    } finally {
      setLogLoading(false);
    }
  };

  /* ── Download ── */
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (applied.purpose.trim()) params.purpose = applied.purpose.trim();
      if (applied.createdBy.trim()) params.createdBy = applied.createdBy.trim();
      if (applied.dateFrom) params.dateFrom = applied.dateFrom;
      if (applied.dateTo) params.dateTo = applied.dateTo;
      if (applied.name.trim()) params.name = applied.name.trim();
      if (applied.designation.trim()) params.designation = applied.designation.trim();
      if (applied.companyName.trim()) params.companyName = applied.companyName.trim();
      if (applied.location.trim()) params.location = applied.location.trim();
      if (applied.status && applied.status.length > 0) params.status = applied.status.join(',');
      if (applied.interaction) params.interaction = applied.interaction;
      if (domainFilter) params.domain = domainFilter;

      const res = await downloadContacts(params);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'contacts.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  /* ── Helpers ── */
  const SortIcon = ({ field }) => {
    if (field !== applied.sortField) return (
      <svg className="ct-sort-icon neutral" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="7 15 12 20 17 15" opacity="0.4" />
        <polyline points="7 9 12 4 17 9" opacity="0.4" />
      </svg>
    );
    return applied.sortDir === 'asc' ? (
      <svg className="ct-sort-icon active" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="7 9 12 4 17 9" />
      </svg>
    ) : (
      <svg className="ct-sort-icon active" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="7 15 12 20 17 15" />
      </svg>
    );
  };

  const formatDate = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatDateShort = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  /* ── Render ── */
  return (
    <div className="ct-root">

      {/* Toolbar */}
      <div className="ct-toolbar">
        <div className="ct-search-wrap">
          <svg className="ct-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="ct-search"
            placeholder="Search name, email, company…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
          {search && (
            <button className="ct-search-clear" onClick={() => { setSearch(''); setPage(0); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        <button className={`ct-filter-btn ${activeFilterCount > 0 ? 'ct-filter-btn--active' : ''}`} onClick={openDrawer}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          Filters
          {activeFilterCount > 0 && <span className="ct-filter-badge">{activeFilterCount}</span>}
        </button>

        {isAdmin && (
          <button className="ct-download-btn" onClick={handleDownload} disabled={downloading}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {downloading ? 'Downloading…' : 'Export Excel'}
          </button>
        )}
      </div>

      {domainFilter && (
        <div className="ct-chips">
          <span className="ct-chip ct-chip--company">
            <svg style={{ width: 12, height: 12 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            </svg>
            Domain: @{domainFilter}
            {onClearDomainFilter && <button onClick={onClearDomainFilter}>✕</button>}
          </span>
        </div>
      )}

      {activeFilterCount > 0 && (
        <div className="ct-chips">
          {applied.name && <span className="ct-chip">Name: {applied.name} <button onClick={() => { setApplied(a => ({ ...a, name: '' })); setPage(0); }}>✕</button></span>}
          {applied.designation && <span className="ct-chip">Designation: {applied.designation} <button onClick={() => { setApplied(a => ({ ...a, designation: '' })); setPage(0); }}>✕</button></span>}
          {applied.companyName && <span className="ct-chip">Company: {applied.companyName} <button onClick={() => { setApplied(a => ({ ...a, companyName: '' })); setPage(0); }}>✕</button></span>}
          {applied.location && <span className="ct-chip">Location: {applied.location} <button onClick={() => { setApplied(a => ({ ...a, location: '' })); setPage(0); }}>✕</button></span>}
          {applied.status.length > 0 && <span className="ct-chip">Status: {applied.status.join(', ')} <button onClick={() => { setApplied(a => ({ ...a, status: [] })); setPage(0); }}>✕</button></span>}
          {applied.purpose && <span className="ct-chip">Purpose: {applied.purpose} <button onClick={() => { setApplied(a => ({ ...a, purpose: '' })); setPage(0); }}>✕</button></span>}
          {applied.createdBy && <span className="ct-chip">Uploaded by: {applied.createdBy} <button onClick={() => { setApplied(a => ({ ...a, createdBy: '' })); setPage(0); }}>✕</button></span>}
          {applied.dateFrom && <span className="ct-chip">From: {applied.dateFrom} <button onClick={() => { setApplied(a => ({ ...a, dateFrom: '' })); setPage(0); }}>✕</button></span>}
          {applied.dateTo && <span className="ct-chip">To: {applied.dateTo} <button onClick={() => { setApplied(a => ({ ...a, dateTo: '' })); setPage(0); }}>✕</button></span>}
          {applied.interaction && <span className="ct-chip">Interaction: {applied.interaction} <button onClick={() => { setApplied(a => ({ ...a, interaction: '' })); setPage(0); }}>✕</button></span>}
          <button className="ct-chip-clear" onClick={resetFilters}>Clear all</button>
        </div>
      )}

      {error && <div className="ct-error">{error}</div>}

      {!loading && totalElements > 0 && (
        <p className="ct-summary">
          Showing <strong>{contacts.length}</strong> of <strong>{totalElements.toLocaleString()}</strong> contacts
        </p>
      )}

      {/* Table */}
      <div className="ct-table-wrap">
        {loading ? (
          <div className="ct-loading"><div className="ct-spinner" /><span>Loading contacts…</span></div>
        ) : contacts.length === 0 ? (
          <div className="ct-empty">
            <div className="ct-empty-icon">👤</div>
            <span>No contacts found</span>
            <small>Try adjusting your search or filters</small>
          </div>
        ) : (
          <table className="ct-table">
            <thead>
              <tr>
                <th className="ct-th ct-th-sm">#</th>
                <th className="ct-th ct-sortable" onClick={() => handleSort('firstName')}>
                  <div className="ct-th-inner">Name <SortIcon field="firstName" /></div>
                </th>
                <th className="ct-th ct-sortable" onClick={() => handleSort('email')}>
                  <div className="ct-th-inner">Email <SortIcon field="email" /></div>
                </th>
                <th className="ct-th">Designation</th>
                <th className="ct-th ct-sortable" onClick={() => handleSort('companyName')}>
                  <div className="ct-th-inner">Company <SortIcon field="companyName" /></div>
                </th>
                <th className="ct-th">Location</th>
                <th className="ct-th">Purpose</th>
                <th className="ct-th">Status</th>
                <th className="ct-th">Uploaded By</th>
                <th className="ct-th ct-sortable" onClick={() => handleSort('createdAt')}>
                  <div className="ct-th-inner">Created <SortIcon field="createdAt" /></div>
                </th>
                <th className="ct-th ct-th-action">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c, idx) => {
                const initials = `${c.firstName?.[0] ?? ''}${c.lastName?.[0] ?? ''}`.toUpperCase();
                return (
                  <tr key={c.id} className="ct-tr">
                    <td className="ct-td ct-td-muted"><span className="ct-row-num">{page * pageSize + idx + 1}</span></td>
                    <td className="ct-td">
                      <div className="ct-name-cell">
                        <div className="ct-avatar">{initials || '?'}</div>
                        <div>
                          <div className="ct-name">{c.firstName} {c.lastName}</div>
                          <div className="ct-sub">{c.designation}</div>
                        </div>
                      </div>
                    </td>
                    <td className="ct-td"><a className="ct-link" href={`mailto:${c.email}`}>{c.email}</a></td>
                    <td className="ct-td ct-td-muted">{c.designation}</td>
                    <td className="ct-td">
                      <div className="ct-name">{c.companyName}</div>
                      <div className="ct-sub">{c.industry}</div>
                    </td>
                    <td className="ct-td ct-td-muted">{c.location}</td>
                    <td className="ct-td"><span className="ct-badge">{c.purpose}</span></td>
                    <td className="ct-td">
                      <span className={`ct-status-badge ct-status-badge--${(c.status ?? 'ACTIVE').toLowerCase()}`}>
                        {c.status ?? 'ACTIVE'}
                      </span>
                    </td>
                    <td className="ct-td ct-td-muted">{c.createdBy}</td>
                    <td className="ct-td ct-td-muted">{formatDate(c.createdAt)}</td>
                    <td className="ct-td ct-td-action">
                      <button className="ct-view-btn" onClick={() => openView(c)} title="View">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      <button className="ct-edit-btn" onClick={() => openEdit(c)} title="Edit">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalElements > 0 && (
        <div className="ct-pagination">
          <div className="ct-page-left">
            <span className="ct-page-info">Page {page + 1} of {totalPages}</span>
          </div>
          <div className="ct-page-right">
            <button className="ct-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i)
              .filter(i => i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 2)
              .reduce((acc, i, idx, arr) => {
                if (idx > 0 && arr[idx - 1] !== i - 1) acc.push('…');
                acc.push(i);
                return acc;
              }, [])
              .map((item, idx) =>
                item === '…'
                  ? <span key={`e-${idx}`} className="ct-page-ellipsis">…</span>
                  : <button key={item} className={`ct-page-num ${item === page ? 'active' : ''}`} onClick={() => setPage(item)}>{item + 1}</button>
              )}
            <button className="ct-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      )}

      {/* ── Filter Drawer ── */}
      {drawerOpen && (
        <div className="ct-drawer-backdrop" onClick={closeDrawer}>
          <div className="ct-drawer" onClick={e => e.stopPropagation()}>
            <div className="ct-drawer-header">
              <h3 className="ct-drawer-title">Filters</h3>
              <button className="ct-modal-close" onClick={closeDrawer}>✕</button>
            </div>
            <div className="ct-drawer-body">
              <div className="ct-drawer-section">
                <label className="ct-form-label">Name</label>
                <div className="ct-input-clear-wrap">
                  <input className="ct-form-input" name="name" placeholder="e.g. John Doe" value={draft.name} onChange={handleDraft} />
                  {draft.name && <button className="ct-input-clear" type="button" onClick={() => setDraft(d => ({ ...d, name: '' }))}>✕</button>}
                </div>
              </div>
              <div className="ct-drawer-section">
                <label className="ct-form-label">Designation</label>
                <div className="ct-input-clear-wrap">
                  <input className="ct-form-input" name="designation" placeholder="e.g. Software Engineer" value={draft.designation} onChange={handleDraft} />
                  {draft.designation && <button className="ct-input-clear" type="button" onClick={() => setDraft(d => ({ ...d, designation: '' }))}>✕</button>}
                </div>
              </div>
              <div className="ct-drawer-section">
                <label className="ct-form-label">Company</label>
                <div className="ct-input-clear-wrap">
                  <input className="ct-form-input" name="companyName" placeholder="e.g. Acme Corp" value={draft.companyName} onChange={handleDraft} />
                  {draft.companyName && <button className="ct-input-clear" type="button" onClick={() => setDraft(d => ({ ...d, companyName: '' }))}>✕</button>}
                </div>
              </div>
              <div className="ct-drawer-section">
                <label className="ct-form-label">Location / Country</label>
                <div className="ct-input-clear-wrap">
                  <input className="ct-form-input" name="location" placeholder="e.g. New York" value={draft.location} onChange={handleDraft} />
                  {draft.location && <button className="ct-input-clear" type="button" onClick={() => setDraft(d => ({ ...d, location: '' }))}>✕</button>}
                </div>
              </div>
              <div className="ct-drawer-section">
                <label className="ct-form-label">Status</label>
                <div className="ct-multi-select">
                  <div
                    className={`ct-multi-select-trigger ${statusOpen ? 'active' : ''}`}
                    onClick={() => setStatusOpen(!statusOpen)}
                  >
                    <span className={`ct-multi-select-text ${draft.status.length === 0 ? 'placeholder' : ''}`}>
                      {draft.status.length === 0
                        ? 'Select Statuses'
                        : draft.status.join(', ')}
                    </span>
                    <svg className="ct-multi-select-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                  {statusOpen && (
                    <div className="ct-multi-select-menu">
                      {STATUS_OPTIONS.map(s => (
                        <div
                          key={s}
                          className={`ct-multi-select-item ${draft.status.includes(s) ? 'selected' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const newStatus = draft.status.includes(s)
                              ? draft.status.filter(item => item !== s)
                              : [...draft.status, s];
                            setDraft(d => ({ ...d, status: newStatus }));
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={draft.status.includes(s)}
                            readOnly
                          />
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="ct-drawer-section">
                <label className="ct-form-label">Purpose</label>
                <div className="ct-input-clear-wrap">
                  <input className="ct-form-input" name="purpose" placeholder="e.g. Sales Outreach" value={draft.purpose} onChange={handleDraft} />
                  {draft.purpose && <button className="ct-input-clear" type="button" onClick={() => setDraft(d => ({ ...d, purpose: '' }))}>✕</button>}
                </div>
              </div>
              <div className="ct-drawer-section">
                <label className="ct-form-label">Interaction</label>
                <select className="ct-form-input ct-form-select" name="interaction" value={draft.interaction} onChange={handleDraft}>
                  <option value="">All</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div className="ct-drawer-section">
                <label className="ct-form-label">Date Range</label>
                <div className="ct-date-row">
                  <div className="ct-date-field">
                    <span className="ct-date-label">From</span>
                    <input className="ct-form-input" type="date" name="dateFrom" value={draft.dateFrom} onChange={handleDraft} />
                  </div>
                  <div className="ct-date-field">
                    <span className="ct-date-label">To</span>
                    <input className="ct-form-input" type="date" name="dateTo" value={draft.dateTo} onChange={handleDraft} />
                  </div>
                </div>
              </div>
            </div>
            <div className="ct-drawer-footer">
              <button className="ct-btn ct-btn-ghost" onClick={resetFilters}>Reset</button>
              <button className="ct-btn ct-btn-primary" onClick={applyFilters}>Apply Filters</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editContact && (
        <div className="ct-modal-backdrop" onClick={closeEdit}>
          <div className="ct-modal" onClick={e => e.stopPropagation()}>
            <div className="ct-modal-header">
              <h2 className="ct-modal-title">Edit Contact</h2>
              <button className="ct-modal-close" onClick={closeEdit}>✕</button>
            </div>
            {editError && <div className="ct-modal-error">{editError}</div>}
            <div className="ct-modal-body">
              <div className="ct-form-grid">
                {[
                  { name: 'firstName', label: 'First Name' },
                  { name: 'lastName', label: 'Last Name' },
                  { name: 'email', label: 'Email', type: 'email' },
                  { name: 'designation', label: 'Designation' },
                  { name: 'linkedinUrl', label: 'LinkedIn URL' },
                  { name: 'companyName', label: 'Company Name' },
                  { name: 'companyUrl', label: 'Company URL' },
                  { name: 'industry', label: 'Industry' },
                  { name: 'companyLinkedinUrl', label: 'Company LinkedIn' },
                  { name: 'location', label: 'Location' },
                  { name: 'employeeCount', label: 'Employee Count' },
                  { name: 'purpose', label: 'Purpose' },
                ].map(({ name, label, type = 'text' }) => (
                  <div key={name} className="ct-form-field">
                    <label className="ct-form-label">{label}</label>
                    <div className="ct-input-clear-wrap">
                      <input className="ct-form-input" type={type} name={name} value={editForm[name]} onChange={handleEditChange} />
                      {editForm[name] && <button className="ct-input-clear" type="button" onClick={() => setEditForm(f => ({ ...f, [name]: '' }))}>✕</button>}
                    </div>
                  </div>
                ))}
                <div className="ct-form-field">
                  <label className="ct-form-label">Status</label>
                  <select className="ct-form-input ct-form-select" name="status" value={editForm.status || 'ACTIVE'} onChange={handleEditChange}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="ct-modal-footer">
              <button className="ct-btn ct-btn-ghost" onClick={closeEdit} disabled={editLoading}>Cancel</button>
              <button className="ct-btn ct-btn-primary" onClick={handleEditSave} disabled={editLoading}>
                {editLoading ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {viewContact && (
        <div className="ct-modal-backdrop" onClick={closeView}>
          <div className="ct-view-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="ct-view-header">
              <div className="ct-view-title-row">
                <div className="ct-view-avatar">
                  {`${viewContact.firstName?.[0] ?? ''}${viewContact.lastName?.[0] ?? ''}`.toUpperCase() || '?'}
                </div>
                <div>
                  <h2 className="ct-view-name">{viewContact.firstName} {viewContact.lastName}</h2>
                  <p className="ct-view-sub">{viewContact.designation} {viewContact.companyName ? `· ${viewContact.companyName}` : ''}</p>
                </div>
              </div>
              <button className="ct-modal-close" onClick={closeView}>✕</button>
            </div>

            {/* Tabs */}
            <div className="ct-view-tabs">
              {[
                { key: 'details', label: 'Details' },
                { key: 'interactions', label: `Interaction History${interactions.length > 0 ? ` (${interactions.length})` : ''}` },
                { key: 'audit', label: 'Audit Trail' },
              ].map(t => (
                <button
                  key={t.key}
                  className={`ct-view-tab ${viewTab === t.key ? 'active' : ''}`}
                  onClick={() => setViewTab(t.key)}
                >
                  {t.key === 'interactions' && (
                    <svg style={{ width: 14, height: 14, marginRight: 4 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                      <path d="M3 3v5h5" /><path d="M12 7v5l4 2" />
                    </svg>
                  )}
                  {t.label}
                </button>
              ))}
            </div>

            {/* Tab: Details */}
            {viewTab === 'details' && (
              <div className="ct-view-body">
                <div className="ct-detail-grid">
                  <div className="ct-detail-item">
                    <span className="ct-detail-label">Email</span>
                    <a className="ct-link" href={`mailto:${viewContact.email}`}>{viewContact.email || '—'}</a>
                  </div>
                  <div className="ct-detail-item">
                    <span className="ct-detail-label">Company</span>
                    <span className="ct-detail-value">{viewContact.companyName || '—'}</span>
                  </div>
                  <div className="ct-detail-item">
                    <span className="ct-detail-label">Designation</span>
                    <span className="ct-detail-value">{viewContact.designation || '—'}</span>
                  </div>
                  <div className="ct-detail-item">
                    <span className="ct-detail-label">Location</span>
                    <span className="ct-detail-value">{viewContact.location || '—'}</span>
                  </div>
                  <div className="ct-detail-item">
                    <span className="ct-detail-label">Purpose</span>
                    <span className="ct-detail-value">{viewContact.purpose || '—'}</span>
                  </div>
                  <div className="ct-detail-item">
                    <span className="ct-detail-label">Last Outreach</span>
                    <span className="ct-detail-value">{formatDateShort(viewContact.updatedAt ?? viewContact.createdAt)}</span>
                  </div>
                  <div className="ct-detail-item">
                    <span className="ct-detail-label">Industry</span>
                    <span className="ct-detail-value">{viewContact.industry || '—'}</span>
                  </div>
                  <div className="ct-detail-item">
                    <span className="ct-detail-label">Employee Count</span>
                    <span className="ct-detail-value">{viewContact.employeeCount || '—'}</span>
                  </div>
                  {viewContact.linkedinUrl && (
                    <div className="ct-detail-item">
                      <span className="ct-detail-label">LinkedIn</span>
                      <a className="ct-link" href={viewContact.linkedinUrl} target="_blank" rel="noreferrer">{viewContact.linkedinUrl}</a>
                    </div>
                  )}
                  {viewContact.companyUrl && (
                    <div className="ct-detail-item">
                      <span className="ct-detail-label">Company URL</span>
                      <a className="ct-link" href={viewContact.companyUrl} target="_blank" rel="noreferrer">{viewContact.companyUrl}</a>
                    </div>
                  )}
                  <div className="ct-detail-item ct-detail-item--full">
                    <span className="ct-detail-label">Status</span>
                    <span className={`ct-status-badge ct-status-badge--${(viewContact.status ?? 'ACTIVE').toLowerCase()}`}>
                      {viewContact.status ?? 'ACTIVE'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Tab: Interaction History */}
            {viewTab === 'interactions' && (
              <div className="ct-view-body">
                <div className="ct-int-toolbar">
                  <button className="ct-log-btn" onClick={openLog}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Log Interaction
                  </button>
                </div>
                {intLoading && <div className="ct-loading"><div className="ct-spinner" /><span>Loading…</span></div>}
                {intError && <div className="ct-error">{intError}</div>}
                {!intLoading && interactions.length === 0 && (
                  <div className="ct-int-empty">No interactions logged yet.</div>
                )}
                <div className="ct-int-list">
                  {interactions.map(i => (
                    <div key={i.id} className="ct-int-card">
                      <div className="ct-int-card-top">
                        <div className="ct-int-badges">
                          <span className="ct-int-badge" style={{ background: badgeColor(CHANNEL_COLORS, i.channel) }}>{i.channel}</span>
                          <span className="ct-int-badge ct-int-badge--outline" style={{ color: badgeColor(OUTCOME_COLORS, i.outcome), borderColor: badgeColor(OUTCOME_COLORS, i.outcome) }}>{i.outcome}</span>
                        </div>
                        <span className="ct-int-date">{formatDateShort(i.createdAt)}</span>
                      </div>
                      <p className="ct-int-action">{i.actionTaken}</p>
                      {i.notes && <p className="ct-int-notes">{i.notes}</p>}
                      <p className="ct-int-by">Logged by: {i.createdBy}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tab: Audit Trail */}
            {viewTab === 'audit' && (
              <div className="ct-view-body">
                <div className="ct-audit-list">
                  {[
                    { label: 'Created By', value: viewContact.createdBy || '—' },
                    { label: 'Created On', value: formatDate(viewContact.createdAt) },
                    { label: 'Last Modified By', value: viewContact.updatedBy || '—' },
                    { label: 'Last Modified On', value: formatDate(viewContact.updatedAt) },
                  ].map(row => (
                    <div key={row.label} className="ct-audit-row">
                      <span className="ct-audit-label">{row.label}</span>
                      <span className="ct-audit-value">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Log Interaction Sub-Modal ── */}
      {showLogModal && (
        <div className="ct-modal-backdrop" style={{ zIndex: 1100 }} onClick={closeLog}>
          <div className="ct-modal ct-log-modal" onClick={e => e.stopPropagation()}>
            <div className="ct-modal-header">
              <h2 className="ct-modal-title">Log Interaction</h2>
              <button className="ct-modal-close" onClick={closeLog}>✕</button>
            </div>
            {logError && <div className="ct-modal-error">{logError}</div>}
            <div className="ct-modal-body">
              <div className="ct-form-field">
                <label className="ct-form-label">Channel</label>
                <select className="ct-form-input ct-form-select" name="channel" value={logForm.channel} onChange={handleLogChange}>
                  {CHANNELS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="ct-form-field" style={{ marginTop: 14 }}>
                <label className="ct-form-label">Action Taken <span style={{ color: '#dc2626' }}>*</span></label>
                <div className="ct-input-clear-wrap">
                  <input className="ct-form-input" name="actionTaken" placeholder="e.g. Sent intro email, Had a demo call…" value={logForm.actionTaken} onChange={handleLogChange} />
                  {logForm.actionTaken && <button className="ct-input-clear" type="button" onClick={() => setLogForm(f => ({ ...f, actionTaken: '' }))}>✕</button>}
                </div>
              </div>
              <div className="ct-form-field" style={{ marginTop: 14 }}>
                <label className="ct-form-label">Notes</label>
                <textarea className="ct-form-input ct-form-textarea" name="notes" placeholder="Additional details…" value={logForm.notes} onChange={handleLogChange} />
              </div>
              <div className="ct-form-field" style={{ marginTop: 14 }}>
                <label className="ct-form-label">Outcome</label>
                <select className="ct-form-input ct-form-select" name="outcome" value={logForm.outcome} onChange={handleLogChange}>
                  {OUTCOMES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="ct-modal-footer">
              <button className="ct-btn ct-btn-ghost" onClick={closeLog} disabled={logLoading}>Cancel</button>
              <button className="ct-btn ct-btn-primary" onClick={handleLogSubmit} disabled={logLoading}>
                {logLoading ? 'Logging…' : 'Log Interaction'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
